<script lang="tsx">
import { defineComponent, reactive, toRefs } from 'vue'
import { ElDropdown, ElDropdownItem, ElDropdownMenu, ElIcon, ElMessageBox } from 'element-plus'
import { CirclePlusFilled } from '@element-plus/icons-vue'
import { Graph, Node, StringExt } from '@antv/x6'
import { defaultNode } from '@/components/business/BusinessFlowSetting/defaultData'
import businessImg from '@/assets/images/flow/business.png'
import peerImg from '@/assets/images/flow/peer.png'
import childImg from '@/assets/images/flow/child.png'
import { NodeType } from '@/enums/flow'

type PositionType = 'top' | 'bottom'
// 节点位置信息
interface Position {
  x: number
  y: number
}
const props = {
  node: {
    type: Node
  }
}
const positiom = ['top', 'bottom']
const flowConfig = [
  {
    icon: NodeType.BUSINESS,
    label: '业务节点',
    name: '业务节点',
    type: NodeType.BUSINESS
  },
  {
    icon: NodeType.CHILD,
    label: '子流程节点',
    name: '子流程节点',
    type: NodeType.CHILD
  },
  {
    icon: NodeType.PEER,
    label: '同级节点',
    name: '同级节点',
    type: NodeType.PEER
  }
]
// 同级节点不能增加同级节点
// 子流程不能增加子流程
const itemContent = (type: NodeType) => (
  <div class="dropWrap">
    <ElDropdownMenu>
      {flowConfig.map(item => (
        <>
          {type === NodeType.PEER && item.type === NodeType.PEER ? (
            ''
          ) : type === NodeType.CHILD && item.type === NodeType.CHILD ? (
            ''
          ) : (
            <ElDropdownItem command={item.type} key={item.type}>
              {item.label}
            </ElDropdownItem>
          )}
        </>
      ))}
    </ElDropdownMenu>
  </div>
)

export default defineComponent({
  name: 'FlowCmp',
  props,
  setup(props) {
    const { node } = props
    let flowData = reactive({ current: node.getData() })
    node.on('change:data', e => {
      flowData.current = e.current
    })
    // 鼠标进入矩形主区域的时候显示连接桩
    const onMainMouseEnter = () => {
      const { node } = props
      // 获取该节点下的所有连接桩
      const ports = node.getPorts() || []
      ports.forEach(port => {
        node.setPortProp(port.id, 'attrs/circle', {
          fill: '#fff',
          stroke: '#85A5FF'
        })
      })
    }
    // 鼠标离开矩形主区域的时候隐藏连接桩
    const onMainMouseLeave = () => {
      const { node } = props
      // 获取该节点下的所有连接桩
      const ports = node.getPorts() || []
      ports.forEach(port => {
        node.setPortProp(port.id, 'attrs/circle', {
          fill: 'transparent',
          stroke: 'transparent'
        })
      })
    }
    /**
     * 创建节点并添加到画布
     * @param graph
     * @param position 节点位置
     * @returns
     */

    const createNode = (graph: Graph, position: Position, command: string) => {
      if (!graph) {
        return {}
      }
      let newNode = {}
      const id = StringExt.uuid()
      const data = flowConfig.find(el => el.type === command) || {}
      const node = {
        id,
        shape: 'flow-comp',
        x: position?.x,
        y: position?.y,
        ports: getPorts(id),
        data: {
          ...data,
          ...defaultNode
        }
      }
      newNode = graph.addNode(node)
      return newNode
    }
    const getPorts = (id: string) => {
      return [
        { id: `${id}-top`, group: 'top' },
        { id: `${id}-bottom`, group: 'bottom' },
        { id: `${id}-left`, group: 'left' },
        { id: `${id}-right`, group: 'right' }
      ]
    }
    /**
     * 创建边并添加到画布
     * @param source
     * @param target
     * @param graph
     */
    const createEdge = (source: string, target: string, graph: Graph, type: PositionType) => {
      let oppositeV
      ;['left,right', 'top,bottom'].some(item => {
        const [s, e] = item.split(',')
        oppositeV = item.split(',').indexOf(type) ? s : e
        return item.includes(type)
      })
      const edge = {
        id: StringExt.uuid(),
        shape: 'customer-edge',
        source: {
          cell: source,
          port: `${target}-${type}`
        },
        target: {
          cell: target,
          port: `${target}-${oppositeV}`
        },
        zIndex: -1,
        data: {
          source,
          target
        }
      }
      if (graph) {
        graph.addEdge(edge)
      }
    }

    // 删除当前节点
    const close = () => {
      const { node } = props
      const { graph } = node?.model || {}
      if (graph) {
        ElMessageBox.confirm('是否确认删除选中节点？', '提示', {
          type: 'warning'
        }).then(() => {
          graph!.removeCells([node] as any)
        })
      }
    }
    // 计算位置
    const getDownstreamNodePosition = (
      node: Node,
      graph: Graph,
      type: PositionType,
      dx = 50,
      dy = 100
    ) => {
      // 获取当前位置
      const downstreamNodeIdList: string[] = []
      graph.getEdges().forEach(edge => {
        const { source, target } = edge.toJSON()
        // const originEdge = edge.toJSON()?.data
        if (source.cell === node.id) {
          downstreamNodeIdList.push(target.cell)
        }
      })
      // 获取起点的位置信息
      const position = node.getPosition()
      return {
        x: position.x + dx,
        y: position.y + dy
      }
    }
    // 创建下游的节点和边
    const createDownstream = (command, type) => {
      const { node } = props
      const { graph } = node.model || {}
      if (graph) {
        let dx = 50
        let dy = -100
        if (type === 'bottom') {
          dx = 110
          dy = 100
        }
        // 获取下游节点的初始位置信息
        const position = getDownstreamNodePosition(node, graph, type, dx, dy)
        // 创建下游节点
        const newNode = createNode(graph, position, command)
        const source = node.id
        const target = newNode.id
        // 创建该节点出发到下游节点的边
        createEdge(source, target, graph, type)
      }
    }
    //
    const handleDropDownClick = (command: string, key: PositionType) => {
      // const { node } = props
      // const { graph } = node.model || {}
      createDownstream(command, key)
    }
    const { current } = toRefs(flowData)

    return () => (
      <div>
        <div
          class={`flow_wrap ${current.value.error ? 'flow_wrap__error' : ''}`}
          onMouseenter={onMainMouseEnter}
          onMouseleave={onMainMouseLeave}
        >
          <div class="flow_chat">
            <div class="tw-flex tw-justify-between tw-items-center tw-w-full">
              <div class="tw-flex-1 tw-flex tw-items-center">
                <div class="flow_icon">
                  <img
                    src={
                      current.value.icon === NodeType.BUSINESS
                        ? businessImg
                        : current.value.icon === NodeType.PEER
                          ? peerImg
                          : childImg
                    }
                  ></img>
                </div>
                <span class="node-name">{current.value?.name || '请在属性配置'}</span>
              </div>
              {current.value.isRoot || current.value.readonly ? (
                ''
              ) : (
                <div class="close" onClick={close}>
                  <span class="icon iconfont icon-close"></span>
                </div>
              )}
            </div>
            {positiom.map(key => (
              <ElDropdown
                placement="bottom-start"
                trigger="click"
                class={['dropdown', key]}
                key={key}
                style={{ [key]: 0 }}
                onCommand={e => handleDropDownClick(e, key)}
              >
                {{
                  default: () => (
                    <span class="el-dropdown-link">
                      <ElIcon color={'#5687FF'}>
                        <CirclePlusFilled />
                      </ElIcon>
                    </span>
                  ),
                  dropdown: () => itemContent(current.value.type)
                }}
              </ElDropdown>
            ))}
          </div>
          <div class="flow-node-default-extent"></div>
        </div>
        <div class="approval_error">{current.value.error}</div>
      </div>
    )
  }
})
</script>
<style scoped lang="scss">
.approval_error {
  margin-top: 2px;
  font-size: 12px;
  color: #f34444;
}
.flow_wrap {
  cursor: move;
  display: flex;
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  border-radius: 5px;
  background: #fff;
  box-shadow: 0 2px 5px 0 #00000014;
  border: 1px solid transparent;
  box-sizing: border-box;
  &__error {
    box-shadow: 0 0 0 2px #fab5b5 !important;
    border: 1px solid #f34444 !important;
  }
  &:hover {
    box-shadow: 0 0 0 3px rgba(48, 129, 242, 0.2);
    border: 1px solid #5687ff;
    box-sizing: border-box;
  }
  .flow_chat {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 8px;
    position: relative;
    .flow_icon {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-right: 8px;
      img {
        width: 20px;
        height: 20px;
      }
    }
    .close {
      position: relative;
      height: 20px;
      width: 20px;
      font-size: 16px;
      z-index: 999;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      flex-shrink: 0;
    }
    .dropdown {
      position: absolute;
      visibility: hidden;
      &.top {
        left: 50%;
        transform: translate(-50%, -120%);
      }
      &.bottom {
        left: 50%;
        transform: translate(-50%, 120%);
      }
      &.left {
        transform: translateX(-120%);
      }
      &.right {
        transform: translateX(120%);
      }
    }
  }
  .node-name {
    color: #333333;
    font-size: 14px;
  }
  .flow-node-default-extent {
    bottom: -15px;
    left: -15px;
    // pointer-events: none;
    position: absolute;
    right: -15px;
    top: -15px;
  }
}
</style>
<style lang="scss">
.x6-edge:hover path:nth-child(2) {
  stroke: #1890ff;
  stroke-width: 1px;
}
.x6-edge-selected path:nth-child(2) {
  stroke: #1890ff;
  stroke-width: 1.5px !important;
}
.x6-node-selected {
  .flow_wrap {
    border: 1px solid #5687ff;
  }
  .dropdown {
    visibility: visible !important;
    z-index: 999;
    cursor: pointer;
  }
  .x6-port {
    visibility: hidden !important;
  }
}
.dropWrap {
  min-width: 132px;
  padding: 12px;
  box-sizing: border-box;
  .el-dropdown-menu {
    padding: 0;
    .el-dropdown-menu__item {
      display: flex;
      padding: 5px 16px;
      justify-content: center;
      align-items: center;
      border-radius: 4px;
      border: 0.5px solid #dcdfe6;
      margin-bottom: 8px;
      color: #333333;
      font-size: 14px;
    }
  }
}
</style>
